import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import tecnicoService from "./services";

const formatTime = (minutos: any) => {
  const val = Number(minutos) || 1;
  const h = Math.floor(val / 60)
    .toString()
    .padStart(2, "0");
  const m = (val % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
};

type AvulsoConfig = Record<
  number,
  { ativo: boolean; ids: Record<string, string> }
>;

type SalvarInstalacaoPayload = {
  formData: any;
  idCliente: number;
  avulsoConfig?: AvulsoConfig;
};

export const salvarInstalacaoCompleta = createAsyncThunk(
  "tecnico/salvarInstalacaoCompleta",
  async (payload: SalvarInstalacaoPayload, thunkAPI) => {
    const { formData, idCliente, avulsoConfig = {} } = payload;

    try {
      const stepIsAvulso = (stepIndex: number) =>
        avulsoConfig[stepIndex]?.ativo === true;

      let idRegionalFinal = Number(formData.loja.idRegional) || 0;

      const regionalFoiPulada =
        !formData.regional ||
        Object.keys(formData.regional).length === 0 ||
        formData.regional.nome === "";

      if (!regionalFoiPulada) {
        const regionalPayload = {
          ...formData.regional,
          idCliente,
          idGerencia: Number(formData.regional.idGerencia) || 1,
          idDivisao: Number(formData.regional.idDivisao) || 1,
          ativo: Number(formData.regional.ativo) === 1 ? 1 : 0,
        };

        const regionalCriada =
          await tecnicoService.criarRegional(regionalPayload);
        const idRegionalGerado =
          typeof regionalCriada === "number" ||
          typeof regionalCriada === "string"
            ? Number(regionalCriada)
            : regionalCriada?.id || regionalCriada?.data?.id;

        if (!idRegionalGerado)
          throw new Error("Falha ao obter ID da Regional criada.");
        idRegionalFinal = idRegionalGerado;
      }

      let idLoja = 0;

      const lojaFoiPulada =
        !formData.loja || Object.keys(formData.loja).length === 0;

      if (!lojaFoiPulada) {
        const lojaPayload = {
          ...formData.loja,
          idCliente,
          idCoordenacao: formData.loja.idCoordenacao || 0,
          idRegional: idRegionalFinal,
          idDivisao: formData.loja.idDivisao || 0,
          arquivoPlanta: formData.loja.arquivoPlanta || "",
          idSeguradora:
            formData.loja.idSeguradora !== "" &&
            formData.loja.idSeguradora !== null
              ? Number(formData.loja.idSeguradora)
              : 0,
          importanciaSegurada: Number(formData.loja.importanciaSegurada) || 0,
          cnpj: formData.loja.cnpj || "",
          vigencia: formData.loja.vigencia
            ? new Date(formData.loja.vigencia).toISOString()
            : null,
          ativo: Number(formData.loja.ativo) === 1 ? 1 : 0,
        };

        const lojaCriada = await tecnicoService.criarLoja(lojaPayload);
        idLoja = lojaCriada.id || lojaCriada.data?.id;
        if (!idLoja) throw new Error("Falha ao obter ID da Loja.");
      }

      const mapaCentraisIds: number[] = [];

      if (stepIsAvulso(1)) {
        (formData.dispcentral ?? []).forEach((_: any, i: number) => {
          mapaCentraisIds[i] = Number(avulsoConfig[1]?.ids?.idCentral) || 0;
        });
      } else if (formData.dispcentral?.length > 0) {
        for (const [index, central] of formData.dispcentral.entries()) {
          const idLojaParaCentral =
            idLoja ||
            Number(avulsoConfig[0]?.ids?.idLoja) ||
            Number((central as any).idLoja) ||
            0;

          const payloadCentral = {
            ...central,
            idLoja: idLojaParaCentral,
            idCliente,
            alertaAtivo:
              central.alertaAtivo === 1 || central.alertaAtivo === true,
            envioEmailAtivo:
              central.envioEmailAtivo === 1 || central.envioEmailAtivo === true,
            envioWhatsAtivo:
              central.envioWhatsAtivo === 1 || central.envioWhatsAtivo === true,
            envioTelegramAtivo:
              central.envioTelegramAtivo === 1 ||
              central.envioTelegramAtivo === true,
            planoAcaoAtivo: false,
            timeMsgEscaladaNV2: "02:00",
            timeMsgEscaladaNV3: "02:52",
            toleranciaOffline: "00:00",
          };

          const resCentral = await tecnicoService.criarCentral(payloadCentral);
          const idGerado = resCentral.id || resCentral.data?.id;
          if (!idGerado)
            throw new Error(`Falha ao criar Central #${index + 1}`);

          mapaCentraisIds[index] = idGerado;
        }
      }

      const mapaModulosReaisIds: any[] = [];

      if (stepIsAvulso(2)) {
        for (const [index, modulo] of (formData.dispmodulo ?? []).entries()) {
          const idLojaFinal =
            Number(avulsoConfig[2]?.ids?.idLoja) || idLoja || 0;
          const idCentralFinal =
            Number(avulsoConfig[2]?.ids?.idCentral) ||
            mapaCentraisIds[(modulo as any).centralIndex || 0] ||
            0;

          const payloadModulo = {
            ...modulo,
            idLoja: idLojaFinal,
            idCliente,
            idCentral: idCentralFinal,
            toleranciaOffline: formatTime((modulo as any).toleranciaOffline),
          };

          const resModulo = await tecnicoService.criarModulo(payloadModulo);
          const idModuloReal = resModulo.id || resModulo.data?.id;

          mapaModulosReaisIds.push({
            indexOriginal: index,
            idReal: idModuloReal,
            adr: Number((modulo as any).adrModulo),
            modelo: (modulo as any).modelo,
            centralIndex: (modulo as any).centralIndex || 0,
          });
        }
      } else if (formData.dispmodulo?.length > 0) {
        for (const [index, modulo] of formData.dispmodulo.entries()) {
          const idCentralPai =
            mapaCentraisIds[(modulo as any).centralIndex || 0];

          const payloadModulo = {
            ...modulo,
            idLoja: idLoja || Number((modulo as any).idLoja) || 0,
            idCliente,
            idCentral: idCentralPai,
            toleranciaOffline: formatTime((modulo as any).toleranciaOffline),
          };

          const resModulo = await tecnicoService.criarModulo(payloadModulo);
          const idModuloReal = resModulo.id || resModulo.data?.id;

          mapaModulosReaisIds.push({
            indexOriginal: index,
            idReal: idModuloReal,
            adr: Number((modulo as any).adrModulo),
            modelo: (modulo as any).modelo,
            centralIndex: (modulo as any).centralIndex || 0,
          });
        }
      }

      const analogicoPromises = (formData.monanalogico ?? []).map(
        async (mon: any) => {
          if (!mon.idEntrada) return;

          let idLojaFinal: number;
          let idCentralFinal: number;
          let idModuloFinal: number;
          let adrModuloFinal: number;
          let modeloModuloFinal: string;

          if (stepIsAvulso(3)) {
            const moduloPaiAvulso = mapaModulosReaisIds[mon.moduloIndex || 0];
            idLojaFinal = Number(avulsoConfig[3]?.ids?.idLoja) || idLoja || 0;
            idCentralFinal =
              Number(avulsoConfig[3]?.ids?.idCentral) ||
              mapaCentraisIds[moduloPaiAvulso?.centralIndex] ||
              0;
            idModuloFinal =
              Number(avulsoConfig[3]?.ids?.idModulo) ||
              moduloPaiAvulso?.idReal ||
              0;
            adrModuloFinal =
              Number(avulsoConfig[3]?.ids?.adrModulo) ||
              moduloPaiAvulso?.adr ||
              0;
            modeloModuloFinal =
              avulsoConfig[3]?.ids?.modeloModulo ||
              moduloPaiAvulso?.modelo ||
              "A8D4";
          } else {
            const moduloPai = mapaModulosReaisIds[mon.moduloIndex || 0];
            if (!moduloPai) return;
            idLojaFinal = idLoja;
            idCentralFinal = mapaCentraisIds[moduloPai.centralIndex] || 0;
            idModuloFinal = moduloPai.idReal;
            adrModuloFinal = moduloPai.adr;
            modeloModuloFinal = moduloPai.modelo;
          }

          return tecnicoService.criarMonAnalogico({
            ...mon,
            idLoja: idLojaFinal,
            idCliente,
            idCentral: idCentralFinal,
            idModulo: idModuloFinal,
            adrModulo: adrModuloFinal,
            modeloModulo: modeloModuloFinal,
            statusOk: true,
            ativo: 1,
          });
        },
      );

      const digitalPromises = (formData.mondigital ?? []).map(
        async (mon: any) => {
          if (!mon.idEntrada) return;

          let idLojaFinal: number;
          let idCentralFinal: number;
          let idModuloFinal: number;
          let adrModuloFinal: number;
          let modeloModuloFinal: string;

          if (stepIsAvulso(4)) {
            const moduloPaiAvulso = mapaModulosReaisIds[mon.moduloIndex || 0];
            idLojaFinal = Number(avulsoConfig[4]?.ids?.idLoja) || idLoja || 0;
            idCentralFinal =
              Number(avulsoConfig[4]?.ids?.idCentral) ||
              mapaCentraisIds[moduloPaiAvulso?.centralIndex] ||
              0;
            idModuloFinal =
              Number(avulsoConfig[4]?.ids?.idModulo) ||
              moduloPaiAvulso?.idReal ||
              0;
            adrModuloFinal =
              Number(avulsoConfig[4]?.ids?.adrModulo) ||
              moduloPaiAvulso?.adr ||
              0;
            modeloModuloFinal =
              avulsoConfig[4]?.ids?.modeloModulo ||
              moduloPaiAvulso?.modelo ||
              "A8D4";
          } else {
            const moduloPai = mapaModulosReaisIds[mon.moduloIndex || 0];
            if (!moduloPai) return;
            idLojaFinal = idLoja;
            idCentralFinal = mapaCentraisIds[moduloPai.centralIndex] || 0;
            idModuloFinal = moduloPai.idReal;
            adrModuloFinal = moduloPai.adr;
            modeloModuloFinal = moduloPai.modelo;
          }

          return tecnicoService.criarMonDigital({
            ...mon,
            idLoja: idLojaFinal,
            idCliente,
            idCentral: idCentralFinal,
            idModulo: idModuloFinal,
            adrModulo: adrModuloFinal,
            modeloModulo: modeloModuloFinal,
            valorPadraoNormal: mon.valorPadraoNormal === 1,
            statusOk: true,
            ativo: 1,
          });
        },
      );

      const medidorPromises = (formData.monmedidor ?? []).map(
        async (mon: any) => {
          if (!mon.idEntrada) return;

          let idLojaFinal: number;
          let idCentralFinal: number;
          let idModuloFinal: number;
          let adrModuloFinal: number;
          let modeloModuloFinal: string;

          if (stepIsAvulso(5)) {
            const moduloPaiAvulso = mapaModulosReaisIds[mon.moduloIndex || 0];
            idLojaFinal = Number(avulsoConfig[5]?.ids?.idLoja) || idLoja || 0;
            idCentralFinal =
              Number(avulsoConfig[5]?.ids?.idCentral) ||
              mapaCentraisIds[moduloPaiAvulso?.centralIndex] ||
              0;
            idModuloFinal =
              Number(avulsoConfig[5]?.ids?.idModulo) ||
              moduloPaiAvulso?.idReal ||
              0;
            adrModuloFinal =
              Number(avulsoConfig[5]?.ids?.adrModulo) ||
              moduloPaiAvulso?.adr ||
              0;
            modeloModuloFinal =
              avulsoConfig[5]?.ids?.modeloModulo ||
              moduloPaiAvulso?.modelo ||
              "A8D4";
          } else {
            const moduloPai = mapaModulosReaisIds[mon.moduloIndex || 0];
            if (!moduloPai) return;
            idLojaFinal = idLoja;
            idCentralFinal = mapaCentraisIds[moduloPai.centralIndex] || 0;
            idModuloFinal = moduloPai.idReal;
            adrModuloFinal = moduloPai.adr;
            modeloModuloFinal = moduloPai.modelo;
          }

          return tecnicoService.criarMonMedidor({
            ...mon,
            idLoja: idLojaFinal,
            idCliente,
            idCentral: idCentralFinal,
            idModulo: idModuloFinal,
            adrModulo: adrModuloFinal,
            modeloModulo: modeloModuloFinal,
            statusOk: true,
            ativo: 1,
          });
        },
      );

      await Promise.all([
        ...analogicoPromises,
        ...digitalPromises,
        ...medidorPromises,
      ]);

      return { success: true };
    } catch (error: any) {
      const message =
        error?.response?.data?.mensagem ||
        error?.message ||
        "Erro na instalação";
      return thunkAPI.rejectWithValue(message);
    }
  },
);

const initialState = {
  loading: false,
  error: null as string | null,
  success: false,
};

const tecnicoSlice = createSlice({
  name: "tecnico",
  initialState,
  reducers: {
    resetStatus: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(salvarInstalacaoCompleta.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(salvarInstalacaoCompleta.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(salvarInstalacaoCompleta.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetStatus } = tecnicoSlice.actions;
export default tecnicoSlice.reducer;
